import { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Link,
  Spinner,
  Tag,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { fetchNextReports43101 } from "../store/reports43101";
import { Reports43101Map } from "./Reports43101Map";
import { config } from "../config";
import { formatList, formatPages, primaryName } from "../utils";
import { Link as RouterLink } from "react-router-dom";

const commodityColors = {
  gold: "yellow",
  silver: "gray",
  copper: "orange",
  zinc: "blue",
  lead: "purple",
  nickel: "green",
  lithium: "cyan",
  cobalt: "pink",
  iron: "red",
  uranium: "teal",
};

const getCommodityColor = (commodity) => {
  const lower = commodity.toLowerCase();
  return commodityColors[lower] || "gray";
};

const TruncatedText = ({ text, maxLength = 200 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text || text.length <= maxLength) {
    return (
      <Text fontSize="sm" color="slate.600" lineHeight="tall">
        {text || "N/A"}
      </Text>
    );
  }

  return (
    <Box>
      <Text fontSize="sm" color="slate.600" lineHeight="tall">
        {expanded ? text : `${text.slice(0, maxLength)}...`}
      </Text>
      <Button
        size="xs"
        variant="link"
        color="amber.600"
        onClick={() => setExpanded(!expanded)}
        mt={1}
        position="relative"
        zIndex={1}
      >
        {expanded ? "Show less" : "Show more"}
      </Button>
    </Box>
  );
};

const highlightSearchTerms = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  // Split search term into individual words
  const words = searchTerm
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length === 0) return text;

  let highlightedText = text;

  // Escape special regex characters and create pattern
  words.forEach((word) => {
    const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedWord})`, "gi");
    highlightedText = highlightedText.replace(
      regex,
      '<mark style="background-color: #FEF3C7; color: #92400E; padding: 2px 4px; border-radius: 3px; font-weight: 600;">$1</mark>'
    );
  });

  return highlightedText;
};

const MatchedSection = ({ section, index }) => {
  const [expanded, setExpanded] = useState(false);
  const searchTerm = useSelector((state) => state.reports43101Slice.searchTerm);
  const highlightedContent = highlightSearchTerms(section.content, searchTerm);

  return (
    <Box
      bg="slate.50"
      borderRadius="lg"
      p={4}
      border="1px solid"
      borderColor="slate.200"
      _hover={{ borderColor: "amber.300", shadow: "sm" }}
      transition="all 0.2s"
      position="relative"
      zIndex={1}
    >
      <Flex justify="space-between" align="start" mb={2}>
        <Box flex={1}>
          <HStack spacing={2} mb={1}>
            {formatPages(section.page) && (
              <Badge colorScheme="amber" fontSize="xs" fontWeight="semibold">
                {formatPages(section.page)}
              </Badge>
            )}
            {section.section_type && section.section_type !== "unclassified" && (
              <Badge colorScheme="blue" fontSize="xs">
                {section.section_type}
              </Badge>
            )}
          </HStack>
          <Text fontWeight="semibold" color="slate.800" fontSize="sm">
            {section.title}
          </Text>
        </Box>
        <Button
          size="xs"
          variant="ghost"
          color="amber.600"
          onClick={() => setExpanded(!expanded)}
          rightIcon={expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        >
          {expanded ? "Collapse" : "Expand"}
        </Button>
      </Flex>

      {expanded && (
        <Box
          mt={3}
          p={3}
          bg="white"
          borderRadius="md"
          border="1px solid"
          borderColor="slate.100"
          maxH="400px"
          overflowY="auto"
        >
          <Text
            fontSize="sm"
            color="slate.700"
            whiteSpace="pre-wrap"
            lineHeight="tall"
            dangerouslySetInnerHTML={{ __html: highlightedContent }}
          />
        </Box>
      )}
    </Box>
  );
};

const ReportCard = ({ report }) => {
  const [showAllSections, setShowAllSections] = useState(false);
  const matchedSections = report.matched_sections || [];
  const visibleSections = showAllSections
    ? matchedSections
    : matchedSections.slice(0, 2);

  const reportUrl = report.id
    ? `${config.API_URL}/43101_reports/${report.id}/pdf`
    : null;

  return (
    // The whole card is clickable via the title link's stretched ::after overlay
    // rather than by wrapping the card in an anchor — a card contains its own
    // links and buttons, and an <a> may not contain another <a>.
    <Box
      position="relative"
      bg="white"
      borderRadius="xl"
      shadow="md"
      overflow="hidden"
      border="1px solid"
      borderColor="slate.200"
      _hover={{ shadow: "lg", borderColor: "amber.400", cursor: "pointer" }}
      transition="all 0.3s"
    >
      {/* Header */}
      <Box bg="linear-gradient(135deg, #1e293b 0%, #334155 100%)" p={5}>
        <Flex justify="space-between" align="start">
          <Box flex={1} pr={4}>
            <Link
              as={RouterLink}
              to={`/reports/${report.id}`}
              textDecoration="none"
              _hover={{ textDecoration: "none" }}
              _after={{
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            >
              <Text
                color="white"
                fontSize="lg"
                fontWeight="bold"
                mb={2}
                noOfLines={2}
              >
                {primaryName(report.project_name, "Untitled Project")}
              </Text>
            </Link>
            <Text color="amber.300" fontSize="sm" fontWeight="medium">
              {formatList(report.company_name, " | ")}
            </Text>
          </Box>
          {reportUrl && (
            <Button
              as="a"
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              colorScheme="amber"
              rightIcon={<ExternalLinkIcon />}
              position="relative"
              zIndex={1}
              flexShrink={0}
            >
              View PDF
            </Button>
          )}
        </Flex>

        {/* Commodities and Report Type */}
        <Wrap mt={3} spacing={2}>
          {report.commodity &&
            report.commodity.length > 0 &&
            report.commodity.map((c, idx) => (
              <WrapItem key={idx}>
                <Tag
                  size="sm"
                  colorScheme={getCommodityColor(c)}
                  borderRadius="full"
                  textTransform="capitalize"
                >
                  {c}
                </Tag>
              </WrapItem>
            ))}
          {report.report_type && (
            <WrapItem>
              <Tag
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                variant="outline"
              >
                {report.report_type}
              </Tag>
            </WrapItem>
          )}
        </Wrap>
      </Box>

      {/* Body */}
      <Box p={5}>
        {/* Meta info */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={5}>
          <GridItem>
            <Text
              fontSize="xs"
              color="slate.500"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Report Date
            </Text>
            <Text fontSize="sm" color="slate.800" fontWeight="medium">
              {report.report_date || "N/A"}
            </Text>
          </GridItem>
          <GridItem>
            <Text
              fontSize="xs"
              color="slate.500"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Location
            </Text>
            <Text fontSize="sm" color="slate.800" fontWeight="medium">
              {report.project_location || "N/A"}
            </Text>
          </GridItem>
        </Grid>

        {/* Deposit Style */}
        {report.deposit_style && (
          <Box mb={4}>
            <Text
              fontSize="xs"
              color="slate.500"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}
            >
              Deposit Style
            </Text>
            <TruncatedText text={report.deposit_style} maxLength={150} />
          </Box>
        )}

        {/* Authors */}
        {report.report_authors?.length > 0 && (
          <Box mb={4}>
            <Text
              fontSize="xs"
              color="slate.500"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}
            >
              Authors
            </Text>
            <TruncatedText
              text={formatList(report.report_authors)}
              maxLength={100}
            />
          </Box>
        )}

        {/* Mineral Resources */}
        {(report.mineral_resources_indicated ||
          report.mineral_resources_inferred) && (
          <Accordion
            allowMultiple
            defaultIndex={[0, 1]}
            mb={4}
            position="relative"
            zIndex={1}
          >
            {report.mineral_resources_indicated && (
              <AccordionItem border="none">
                <AccordionButton px={0} _hover={{ bg: "transparent" }}>
                  <Text
                    fontSize="xs"
                    color="slate.500"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Indicated Resources
                  </Text>
                  <AccordionIcon ml={2} color="slate.500" />
                </AccordionButton>
                <AccordionPanel px={0} pb={2}>
                  <Text fontSize="sm" color="slate.600" lineHeight="tall">
                    {report.mineral_resources_indicated}
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            )}
            {report.mineral_resources_inferred && (
              <AccordionItem border="none">
                <AccordionButton px={0} _hover={{ bg: "transparent" }}>
                  <Text
                    fontSize="xs"
                    color="slate.500"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Inferred Resources
                  </Text>
                  <AccordionIcon ml={2} color="slate.500" />
                </AccordionButton>
                <AccordionPanel px={0} pb={2}>
                  <Text fontSize="sm" color="slate.600" lineHeight="tall">
                    {report.mineral_resources_inferred}
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            )}
          </Accordion>
        )}

        {/* Notes */}
        {report.notes && (
          <Box mb={4}>
            <Text
              fontSize="xs"
              color="slate.500"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}
            >
              Notes
            </Text>
            <TruncatedText text={report.notes} maxLength={200} />
          </Box>
        )}

        {/* Matched Sections */}
        {matchedSections.length > 0 && (
          <Box mt={5}>
            <Divider mb={4} />
            <Text fontSize="sm" color="slate.700" fontWeight="bold" mb={3}>
              Matched Sections ({matchedSections.length})
            </Text>
            <VStack spacing={3} align="stretch">
              {visibleSections.map((section, idx) => (
                <MatchedSection key={idx} section={section} index={idx} />
              ))}
            </VStack>
            {matchedSections.length > 2 && (
              <Button
                variant="ghost"
                color="amber.600"
                size="sm"
                mt={3}
                position="relative"
                zIndex={1}
                onClick={() => setShowAllSections(!showAllSections)}
                rightIcon={
                  showAllSections ? <ChevronUpIcon /> : <ChevronDownIcon />
                }
              >
                {showAllSections
                  ? "Show fewer sections"
                  : `Show ${matchedSections.length - 2} more sections`}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const LoadMoreButton = () => {
  const dispatch = useDispatch();
  const nextPageNum = useSelector(
    (state) => state.reports43101Slice.searchResults
  ).next_page;
  const resultsLoading = useSelector(
    (state) => state.reports43101Slice.loadingResults
  );

  if (!nextPageNum) return null;

  return (
    <Center my={8}>
      <Button
        isLoading={resultsLoading}
        onClick={() => dispatch(fetchNextReports43101())}
        colorScheme="amber"
        size="lg"
        borderRadius="full"
        px={8}
        shadow="md"
        _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        Load More Reports
      </Button>
    </Center>
  );
};

export const Reports43101Body = () => {
  const searchResults =
    useSelector((state) => state.reports43101Slice.searchResults).data || [];
  const searchInitiated = useSelector(
    (state) => state.reports43101Slice.searchInitiated
  );
  const loadingResults = useSelector(
    (state) => state.reports43101Slice.loadingResults
  );
  const numDocs =
    useSelector((state) => state.reports43101Slice.searchResults).num_docs || 0;

  return (
    <Flex
      bg="linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)"
      h="calc(100vh - 80px)"
    >
      {/* Left side - Search Results */}
      <Box
        w="50%"
        h="100%"
        overflowY="auto"
        py={6}
        px={4}
        borderRight="1px solid"
        borderColor="slate.200"
      >
        {/* Results count */}
        {searchInitiated && !loadingResults && searchResults.length > 0 && (
          <Text color="slate.600" mb={4} fontSize="sm">
            Found{" "}
            <Text as="span" fontWeight="bold" color="amber.600">
              {numDocs}
            </Text>{" "}
            reports
          </Text>
        )}

        {/* Results list */}
        <VStack spacing={6} align="stretch">
          {searchResults.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </VStack>

        <LoadMoreButton />

        {/* Empty states */}
        {searchResults.length === 0 && searchInitiated && !loadingResults && (
          <Center flexDirection="column" py={20}>
            <Text fontSize="6xl" mb={4}>
              📊
            </Text>
            <Text fontSize="xl" color="slate.600" fontWeight="medium">
              No reports found
            </Text>
            <Text color="slate.500" mt={2}>
              Try adjusting your search terms or filters
            </Text>
          </Center>
        )}

        {searchResults.length === 0 && searchInitiated && loadingResults && (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="slate.200"
                color="amber.500"
                size="xl"
              />
              <Text color="slate.600">Searching reports...</Text>
            </VStack>
          </Center>
        )}

        {searchResults.length === 0 && !searchInitiated && (
          <Center flexDirection="column" py={20}>
            <Text fontSize="6xl" mb={4}>
              🔍
            </Text>
            <Text fontSize="2xl" color="slate.700" fontWeight="bold" mb={2}>
              Search NI 43-101 Reports
            </Text>
            <Text color="slate.500" textAlign="center" maxW="md">
              Enter keywords to search through technical mining reports. Use
              filters to narrow down by commodity, deposit style, or date range.
            </Text>
          </Center>
        )}
      </Box>

      {/* Right side - Map */}
      <Box w="50%" h="100%" p={4}>
        <Reports43101Map />
      </Box>
    </Flex>
  );
};
